import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { shortDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  MoreHorizontal, Lock, LockOpen, Flower, MessageCircle, Bookmark, Calendar,
  FileText, Trash2, Loader2,
} from "lucide-react";

const REACTIONS = [
  { key: "heart", emoji: "💗" }, { key: "inspired", emoji: "✨" },
  { key: "moved", emoji: "💫" }, { key: "strong", emoji: "💪" },
];

function mediaSrc(raw) {
  if (!raw) return null;
  if (raw.startsWith("http") || raw.startsWith("data:")) return raw;
  return `data:image/jpeg;base64,${raw}`;
}

export default function PostCard({ post, savedInit, onReport, onUnlock, onOpen }) {
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState(post.reactions_summary || {});
  const [myReaction, setMyReaction] = useState(post.my_reaction || null);
  const [showPicker, setShowPicker] = useState(false);
  const [saved, setSaved] = useState(!!savedInit);
  const [commentOpen, setCommentOpen] = useState(false);

  const locked = !!post.is_paid && !post.unlocked;
  const kind = ["image", "video", "audio"].includes(post.content_type) ? post.content_type : "text";
  const total = useMemo(() => Object.values(summary).reduce((a, b) => a + b, 0), [summary]);
  const src = mediaSrc(locked ? post.preview_media_base64 : post.media_base64);

  const react = async (r) => {
    setShowPicker(false);
    const next = myReaction === r ? null : r;
    const prev = myReaction;
    setSummary((s) => { const n = { ...s }; if (prev) n[prev] = Math.max(0, (n[prev] || 0) - 1); if (next) n[next] = (n[next] || 0) + 1; return n; });
    setMyReaction(next);
    try { const { data } = await api.post(`/feed/post/${post.id}/reactions`, { reaction: next }); if (data?.summary) setSummary(data.summary); setMyReaction(data?.my_reaction ?? null); }
    catch { setSummary(post.reactions_summary || {}); setMyReaction(prev); }
  };

  const toggleSave = async () => {
    const prev = saved;
    setSaved(!prev);
    try { if (prev) await api.delete(`/bookmarks/${post.id}`); else await api.post("/bookmarks", { post_id: post.id }); }
    catch (e) { setSaved(prev); toast.error(apiError(e)); }
  };

  const topReactions = REACTIONS.map((r) => ({ ...r, c: summary[r.key] || 0 })).filter((r) => r.c > 0).sort((a, b) => b.c - a.c).slice(0, 3);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`post-card-${post.id}`}>
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {post.coach_photo ? <img src={post.coach_photo} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">{(post.coach_name || "C")[0]}</span>}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{post.coach_name || t("coach")}</p>
            {post.created_at ? <p className="text-xs text-muted-foreground">{shortDate(post.created_at)}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {post.promoted ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">{t("post_sponsored_chip")}</span> : null}
          {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><Lock className="h-2.5 w-2.5" />{t("post_paid_chip")}</span> : null}
          <button onClick={() => onReport(post)} data-testid={`post-report-${post.id}`} className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent"><MoreHorizontal className="h-4 w-4" /></button>
        </div>
      </div>

      <div className={`relative ${onOpen ? "cursor-pointer" : ""}`} onClick={onOpen ? () => onOpen(post) : undefined} data-testid={onOpen ? `post-open-${post.id}` : undefined}>
        {kind === "image" && src ? (
          <img src={src} alt={post.title || ""} className="aspect-square w-full object-cover" />
        ) : (
          <div className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-2 bg-primary/5 p-6 text-center">
            <FileText className="h-7 w-7 text-primary/60" />
            {post.title ? <p className="font-display text-lg text-foreground">{post.title}</p> : null}
            {post.body ? <p className="line-clamp-4 text-sm text-muted-foreground">{post.body}</p> : null}
          </div>
        )}
        {locked ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/45">
            <Lock className="h-7 w-7 text-white" />
            <p className="text-sm font-semibold uppercase tracking-wide text-white">{t("paid_locked")}</p>
          </div>
        ) : null}
      </div>

      <div className="space-y-2 p-4">
        {kind !== "text" && post.title ? <p className="font-display text-base text-foreground">{post.title}</p> : null}
        {post.body ? <p className="text-sm leading-relaxed text-muted-foreground">{locked && post.body.length > 120 ? post.body.slice(0, 120) + "…" : post.body}</p> : null}
        {locked ? (
          <button onClick={() => onUnlock(post)} data-testid={`post-unlock-${post.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <LockOpen className="h-3.5 w-3.5" /> {t("unlock_credits", { n: post.price_credits ?? 0 })}
          </button>
        ) : null}
        {!locked && post.unlocked && post.is_paid ? <p className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">{t("feed_unlocked")}</p> : null}
        {post.booking_cta_product_id ? (
          <a href={`/coaches/${post.coach_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#5E7A73] px-4 py-2 text-xs font-semibold text-white"><Calendar className="h-3.5 w-3.5" /> {post.booking_cta_label || t("post_book_now")}</a>
        ) : null}
      </div>

      {total > 0 ? (
        <div className="flex items-center gap-1.5 px-4 pb-1">
          <span className="flex">{topReactions.map((r, i) => <span key={r.key} className="text-sm" style={{ marginLeft: i === 0 ? 0 : -4 }}>{r.emoji}</span>)}</span>
          <span className="text-xs text-muted-foreground">{total}</span>
        </div>
      ) : null}

      <div className="relative flex items-center gap-5 border-t border-border px-4 py-2.5">
        <button onClick={() => react(myReaction ? null : "heart")} onContextMenu={(e) => { e.preventDefault(); setShowPicker((v) => !v); }} data-testid={`post-react-${post.id}`} className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
          {myReaction ? <span className="text-lg leading-none">{REACTIONS.find((r) => r.key === myReaction)?.emoji}</span> : <Flower className="h-5 w-5" />}
        </button>
        <button onClick={() => setShowPicker((v) => !v)} data-testid={`post-react-more-${post.id}`} className="-ml-4 text-xs text-muted-foreground hover:text-primary">•••</button>
        {showPicker ? (
          <div className="absolute -top-10 left-2 z-10 flex gap-1 rounded-full border border-border bg-card p-1 shadow-md">
            {REACTIONS.map((r) => <button key={r.key} onClick={() => react(r.key)} data-testid={`react-${r.key}-${post.id}`} className={`rounded-full px-1.5 text-lg hover:scale-110 ${myReaction === r.key ? "bg-primary/15" : ""}`}>{r.emoji}</button>)}
          </div>
        ) : null}
        <button onClick={() => setCommentOpen(true)} data-testid={`post-comment-${post.id}`} className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary"><MessageCircle className="h-5 w-5" /></button>
        <button onClick={toggleSave} data-testid={`post-save-${post.id}`} className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary">
          <Bookmark className="h-5 w-5" fill={saved ? "currentColor" : "none"} />
        </button>
      </div>

      <CommentDialog post={post} open={commentOpen} onClose={() => setCommentOpen(false)} t={t} lang={lang} />
    </div>
  );
}

function CommentDialog({ post, open, onClose, t }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/feed/post/${post.id}/comments`); setComments(Array.isArray(data) ? data : []); }
    catch { setComments([]); }
  }, [post.id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try { await api.post(`/feed/post/${post.id}/comments`, { body }); setText(""); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  const remove = async (cid) => {
    try { await api.delete(`/feed/post/${post.id}/comments/${cid}`); setComments((c) => c.filter((x) => x.id !== cid)); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`comment-dialog-${post.id}`}>
        <DialogHeader><DialogTitle>{t("feed_comments_title")}</DialogTitle></DialogHeader>
        <div className="max-h-[50vh] space-y-3 overflow-y-auto">
          {comments === null ? <p className="text-sm text-muted-foreground">…</p> : comments.length === 0 ? (
            <p className="py-6 text-center text-sm italic text-muted-foreground">{t("feed_no_comments")}</p>
          ) : comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{(c.author_name || c.user_name || "·")[0]}</span>
              <div className="min-w-0 flex-1 rounded-2xl bg-secondary/40 px-3 py-2">
                <p className="text-xs font-semibold text-foreground">{c.author_name || c.user_name || ""}</p>
                <p className="text-sm text-foreground">{c.body}</p>
              </div>
              {c.is_mine || c.can_delete ? <button onClick={() => remove(c.id)} className="rounded p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button> : null}
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("feed_write_comment")} className="max-h-24 min-h-[44px] flex-1 resize-none" data-testid={`comment-input-${post.id}`} />
          <Button onClick={send} disabled={!text.trim() || sending} data-testid={`comment-send-${post.id}`} className="rounded-full">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("feed_add_comment")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
